import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import PageLoading from '@/routes/PageLoading';
import {
  contentDeliveryApi,
  useGetContentByFriendlyUrlQuery,
  useGetGlobalManyElementsQuery,
  useLazyGetContentQuery
} from '@/redux/api/contentDeliveryAPI';
import { Page } from '@/lib/ComponentFactory';
import { isEditOrPreviewMode, subcribeContentSavedEvent } from '@/lib/editModeHelpers';
import ErrorPage from '@/pages/ErrorPage';
import { isErrorWithMessage, isFetchBaseQueryError } from '@/redux/helpers';
import useQueryParams from '@/hooks/useQueryParams';
import { expandChildren } from './helpers/expandChildren';
import { setPageLinks, setSiteLabels } from '@/redux/slices/pageSlice';
import { ReferencePageSettings } from '@/@types/content';
import useDebounceLoading from '@/hooks/useDebounceLoading';
import { RootState } from '@/redux/store';

import PageData from './PageData';
import { useAuth } from 'react-oidc-context';
import { isContentType } from './helpers/contentType';
import { simpleHash } from '@/utils/helpers';
import { userRefetchNav } from '@/redux/slices/authSlice';
import { ICreditPlanBlockProps } from '@/components/blocks/CreditPlan/CreditPlanBlock';
import { updateCreditPlanBlock } from '@/redux/slices/creditPlanSlice';

const PageMapper: React.FC = React.memo(() => {
  const authReady = useSelector((state: RootState) => state.auth.ready);
  const refetchNav = useSelector((state: RootState) => state.auth.refetchNav);
  const userId = useSelector((state: RootState) => state.auth.name || 'ANON');

  const params = useParams();
  const modeEdit = isEditOrPreviewMode();
  const dispatch = useDispatch();
  const queryParams = useQueryParams();
  const auth = useAuth();

  const [getError400Content, { data: error400Data, isLoading: isLoadingError400 }] =
    useLazyGetContentQuery();

  const {
    data: itemsContent,
    isLoading: isLoadingGlobal,
    refetch: refetchGlobalElements,
    isFetching
  } = useGetGlobalManyElementsQuery(
    { names: ['footer', 'header', 'labels', 'credit-plans'], userId: simpleHash(userId) },
    { skip: !authReady }
  );

  const footerData = useMemo(() => itemsContent?.['footer'], [itemsContent]);
  const headerData = useMemo(() => itemsContent?.['header'], [itemsContent]);
  const labelsData = useMemo(() => itemsContent?.['labels'], [itemsContent]);
  const creditPlansData = useMemo(() => itemsContent?.['credit-plans'], [itemsContent]);

  const { data, isError, isLoading, isSuccess, currentData, error, refetch } =
    useGetContentByFriendlyUrlQuery({
      friendlyUrl: params['*']!,
      query: {
        expand: ['mainContentArea'],
        ...(queryParams ?? {})
      },
      userId: simpleHash(userId)
    });

  const debouncedLoading = useDebounceLoading(isLoading);

  // Handle classic segments URLs with #SEGV hash
  useEffect(() => {
    const programUrlMatch = window.location.href.match(/^(.*?\/programs\/[^/?#]+)/);
    const segvUrlMatch = window.location.hash.match(/^#SEGV(\d+)$/);
    if (segvUrlMatch && programUrlMatch) {
      const segvValue = segvUrlMatch[1];
      const baseUrl = programUrlMatch[1].replace(/\/$/, '') + '/';
      window.location.href = baseUrl + segvValue;
    }
  });

  useEffect(() => {
    if (refetchNav && (!isFetching || !isLoading)) {
      dispatch(userRefetchNav(false));
      refetchGlobalElements();
    }
  }, [dispatch, isFetching, isLoading, refetchGlobalElements, refetchNav]);

  useEffect(() => {
    if (modeEdit) {
      const communicationScript = document.createElement('script');
      communicationScript.src = `${import.meta.env.VITE_CONTENT_DELIVERY_API}/episerver/cms/latest/clientresources/communicationinjector.js`;
      document.head.appendChild(communicationScript);

      const deliveryPropertyRendererScript = document.createElement('script');
      deliveryPropertyRendererScript.src = `${import.meta.env.VITE_CONTENT_DELIVERY_API}/episerver/cms/latest/clientresources/deliverypropertyrenderer.js`;
      document.head.appendChild(deliveryPropertyRendererScript);

      subcribeContentSavedEvent(message => {
        console.info(message);
        dispatch(contentDeliveryApi.util.invalidateTags([{ type: 'Content', id: params['*'] }]));
        refetch();
      });
    }
  }, [dispatch, modeEdit, params, refetch]);

  useEffect(() => {
    if (labelsData) {
      const siteLabels = labelsData as ReferencePageSettings;
      dispatch(
        setPageLinks({
          Certificates: siteLabels?.myCertificatesPageReference?.url,
          Library: siteLabels.myLibraryPageReference?.url,
          Licenses: siteLabels.myLicensesPageReference?.url,
          Search: siteLabels.searchPageReference?.url,
          PlusMembershipInfo: siteLabels.plusMembershipInfoPageReference?.url,
          Profile: siteLabels.profilePageReference?.url,
          GlobalError: siteLabels.globalErrorPage?.url
        })
      );
      dispatch(setSiteLabels(siteLabels));
    }
  }, [labelsData, dispatch]);
  useEffect(() => {
    if (!creditPlansData) return;
    const creditPlans = creditPlansData as ICreditPlanBlockProps;
    dispatch(
      updateCreditPlanBlock({
        override: true,
        data: creditPlans
      })
    );
  }, [creditPlansData, dispatch]);
  useEffect(() => {
    if (data) {
      //DIAG: code added to prevent using cms auth cookies in request from fe
      // this code technically is not needed but i'll keep only log part
      // auth.signinSilent(); was causing infinite loop when refresh token has expired
      // because same operation was called by Expired event in oidc
      const customerId = (data as Record<string, number>)['customerId'];
      if (customerId != undefined) {
        if (customerId > 0) {
          if (auth?.user?.profile?.IndividualSK) {
            if (customerId != auth.user.profile.IndividualSK) {
              console.info(
                `DIAG:Local User State doesn't match to CMS Authorized identity ${customerId}-${auth.user.profile.IndividualSK}`
              );
              //should logout of CMS here
              //auth.signoutRedirect();
              /*if (navigator.serviceWorker != null) {
                  navigator.serviceWorker.getRegistration().then(swr => {
                    swr?.unregister()?.then(() => {
                      document.location.reload();
                    });
                  });
                }*/
            }
          } else {
            console.info(`DIAG:Local User is logged out but CMS authorized for ${customerId}`);
            //should logout of CMS here
            //auth.signoutRedirect();
          }
        } else if (auth?.isAuthenticated) {
          console.info(
            `DIAG:Token is unauthorized on CMS but user signed in locally as ${auth?.user?.profile?.IndividualSK}`
          );
          //const IndividualSK = auth?.user?.profile?.IndividualSK;
          //auth.isAuthenticated = false;
          //auth.revokeTokens(["access_token"]).then(() => {
          //  console.warn(`Trying renew for ${auth?.user?.profile?.IndividualSK}`);
          //  auth.startSilentRenew();
          //})
          //auth.revokeTokens().then(() => auth.removeUser().then(() => {
          //    auth.clearStaleState().then(() => {
          //        console.warn(`User removed ${IndividualSK}`);
          //        document.location.reload();
          //      }
          //    );
          //  }
          //));

          //auth.signoutRedirect();
          //auth.clearStaleState();
          //auth.signinSilent();
        }
      }
    }
  }, [data, auth, auth.user?.profile]);
  // Handle error state
  if (isError) {
    let content, message, status;

    if (isFetchBaseQueryError(error)) {
      if (error.data) {
        try {
          content = JSON.parse(error.data as string);
          if (message === '[object Object]' || !message) {
            if (content?.message) {
              message = content?.message ?? content;
            } else {
              message = 'Network error connecting to CMS';
            }
          }
          if (content && isContentType(content, 'ErrorPage')) {
            return (
              <Page
                {...expandChildren(content)}
                headerBlock={headerData}
                footerBlock={footerData}
                siteLabels={labelsData}
              />
            );
          }
        } catch (e) {
          // couldn't get data from error
          console.error(e);
        }
      }

      if (!message && 'error' in error) message = error.error;
      status = 'originalStatus' in error ? error.originalStatus : error.status;
      if (message?.toLowerCase()?.includes('[object') || !message) {
        if (content?.message) {
          message = content.message;
        } else {
          message = 'Network error connecting to CMS';
        }
      }
      if (status === 400) {
        const error400Ref = (labelsData as ReferencePageSettings)?.error400;
        if (error400Ref?.id && !error400Data && !isLoadingError400) {
          getError400Content({
            contentLink: error400Ref.id.toString()
          });
          return <PageLoading show={true} />;
        }

        if (error400Data) {
          return (
            <Page
              {...expandChildren(error400Data)}
              headerBlock={headerData}
              footerBlock={footerData}
              siteLabels={labelsData}
            />
          );
        }

        // Fallback to default error page if no error400 reference is set
        return (
          <ErrorPage
            possibleRedirectTo={params['*']}
            message={'400 Bad Request'}
            headerBlock={headerData}
            footerBlock={footerData}
            siteLabels={labelsData}
          />
        );
      }
      if (status === 403)
        return (
          <ErrorPage
            possibleRedirectTo={params['*']}
            message={'403 Access forbidden'}
            headerBlock={headerData}
            footerBlock={footerData}
            siteLabels={labelsData}
          />
        );
      if (status === 404)
        return (
          <ErrorPage
            possibleRedirectTo={params['*']}
            message={'404 Page not found'}
            headerBlock={headerData}
            footerBlock={footerData}
            siteLabels={labelsData}
          />
        );

      if (status === 'TIMEOUT_ERROR')
        return (
          <ErrorPage
            possibleRedirectTo={params['*']}
            message={`Timeout Error`}
            title={`We’re sorry, your request took longer than expected and could not be completed.`}
            copy={`Please try reloading the page. If the issue continues contact customer service at ${import.meta.env.VITE_APP_SERVICE_PHONE_NUMBER} or ${import.meta.env.VITE_APP_INFO_EMAIL}.`}
            headerBlock={headerData}
            footerBlock={footerData}
            siteLabels={labelsData}
          />
        );

      return (
        <ErrorPage
          possibleRedirectTo={params['*']}
          message={`${status} ${message}`}
          headerBlock={headerData}
          footerBlock={footerData}
          siteLabels={labelsData}
        />
      );
    } else if (isErrorWithMessage(error)) {
      return (
        <ErrorPage
          possibleRedirectTo={params['*']}
          message={error.message}
          headerBlock={headerData}
          footerBlock={footerData}
          siteLabels={labelsData}
        />
      );
    }
  }

  // Handle loading/no data state
  if ((debouncedLoading && (!authReady || !isSuccess || !currentData)) || !!isLoadingGlobal)
    return <PageLoading show={true} />;

  return (
    <>
      {data && (
        <Page
          {...{
            ...data,
            headerBlock: headerData,
            footerBlock: footerData,
            siteLabels: labelsData
          }}
        />
      )}
      <PageData>{data}</PageData>
    </>
  );
});

export default PageMapper;
